import React, { useState, useReducer } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OAuthUser } from '../../../site/pbTypes'
import {
  prepareAgreement,
  prepareAddendumAgreement,
  createReservation,
  createLeaseAddendum,
  logErrorToDb,
  pb,
  sendExistingCustomerOTP,
  verifyCustomerOtpResume
} from '../../../api-utils'
import { buildSubmitErrorMessage, computeSubmissionKey } from './utils/reservationSubmitHelpers'
import { recoveryReducer, initialRecoveryState } from './utils/recoveryMachine'
import OtpVerificationStep from './steps/OtpVerificationStep'
import Spinner from '../../../components/Spinner'
import { formatPhoneNumber, calculateProratedTotal, getNextFirstOfMonth } from '../../../utils'
import { PAYMENT_CONSTANTS } from '../../../payment-utils'

import PersonalInfoStep from './steps/PersonalInfoStep'
import AddressInfoStep from './steps/AddressInfoStep'
import VehicleInfoStep from './steps/VehicleInfoStep'
import EmergencyContactStep from './steps/EmergencyContactStep'
import LeaseDetailsStep from './steps/LeaseDetailsStep'
import PricingSummaryStep from './steps/PricingSummaryStep'
import WizardNavigation from './components/WizardNavigation'
import StepProgressIndicator from './components/StepProgressIndicator'

import { storageFormSchema, StorageFormValues, getFieldsToValidateForStep } from './config/validationSchema'
import { getInitialStorageFormValues } from './config/initialValues'
import { getTotalSteps, getStepTitles, getStepId } from './config/stepConfig'

const { PREP_AGREEMENT_TITLE, PLEASE_WAIT } = PAYMENT_CONSTANTS

interface InnerFormProps {
  space: any
  spaceId: string | null
  oauthUser?: OAuthUser
  authenticatedCustomer: any
  isAuthenticated: boolean
  onSetPageTitle: (title: string | undefined) => void
  onSetPageSubtitle: (subtitle: string | undefined) => void
  onSetFirstName: (name: string) => void
  onSetSignPageUrl: (url: string) => void
  onError: (message: string) => void
  onLoginClick: () => void
  onSessionExpired: () => void
  navigate: (path: string) => void
}

const InnerForm: React.FC<InnerFormProps> = ({
  space,
  spaceId,
  oauthUser,
  authenticatedCustomer,
  isAuthenticated,
  onSetPageTitle,
  onSetPageSubtitle,
  onSetFirstName,
  onSetSignPageUrl,
  onError,
  onLoginClick,
  onSessionExpired,
  navigate,
}) => {
  const formIsAuthenticated = isAuthenticated && !!authenticatedCustomer?.id

  // Guards against duplicate eSignatures agreements:
  // - submittingRef: re-entrancy guard so a submit can't run twice concurrently
  // - createdContractRef: caches the agreement created for a given submission so
  //   a retry with unchanged inputs REUSES it instead of minting a new contract.
  const submittingRef = React.useRef(false)
  const createdContractRef = React.useRef<{ key: string; invite: any } | null>(null)
  // Re-entrancy guard so the recovery completion can't run twice concurrently.
  const completingRef = React.useRef(false)

  const [recovery, dispatchRecovery] = useReducer(recoveryReducer, initialRecoveryState)
  // Holds everything needed to COMPLETE the reservation after OTP verification.
  const pendingReservationRef = React.useRef<any>(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    getValues,
    formState: { errors, isSubmitting, isDirty, isValid }
  } = useForm<StorageFormValues>({
    resolver: zodResolver(storageFormSchema),
    defaultValues: getInitialStorageFormValues(oauthUser, authenticatedCustomer),
    mode: 'onChange',
  })

  // Expose form methods for E2E testing
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__RHF_SET_VALUE = setValue;
      (window as any).__RHF_GET_VALUES = getValues;
      (window as any).__RHF_TRIGGER = trigger;
    }
    return () => {
      delete (window as any).__RHF_SET_VALUE;
      delete (window as any).__RHF_GET_VALUES;
      delete (window as any).__RHF_TRIGGER;
    }
  }, [setValue, getValues, trigger])

  // Track form load
  React.useEffect(() => {
    logErrorToDb({
      event_name: 'form_step_reached',
      error_message: 'Step 1: Personal Information',
      form_step: 1,
      payload_snapshot: { space_id: space?.id, step_title: 'Personal Information' },
    })
  }, [])

  const [currentStep, setCurrentStep] = useState(1)
  const totalSteps = getTotalSteps(formIsAuthenticated)
  const stepTitles = getStepTitles(formIsAuthenticated)
  const currentStepId = getStepId(currentStep, formIsAuthenticated)

  const handleNext = async () => {
    const fieldsToValidate = getFieldsToValidateForStep(currentStepId, !!oauthUser)
    const isStepValid = await trigger(fieldsToValidate)

    if (isStepValid && currentStep < totalSteps) {
      const nextStep = currentStep + 1
      const nextStepId = getStepId(nextStep, formIsAuthenticated)
      const nextStepTitle = stepTitles[nextStep - 1]

      // Track step progression
      logErrorToDb({
        event_name: 'form_step_reached',
        error_message: `Step ${nextStep}: ${nextStepTitle}`,
        customer_email: getValues('email'),
        form_step: nextStepId,
        payload_snapshot: { space_id: space?.id, step_title: nextStepTitle },
      })

      setCurrentStep(nextStep)
      window.scrollTo(0, 0)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      window.scrollTo(0, 0)
    }
  }

  const FIELD_LABELS: Record<string, string> = {
    first_name: 'First Name',
    last_name: 'Last Name',
    phone: 'Phone',
    email: 'Email',
    address_line_1: 'Address',
    city: 'City',
    state: 'State',
    zip: 'ZIP Code',
    driver_lic_front: "Driver's License (Front)",
    driver_lic_back: "Driver's License (Back)",
    vehicle_type: 'Vehicle Type',
    vehicle_year: 'Vehicle Year',
    vehicle_make: 'Vehicle Make',
    vehicle_model: 'Vehicle Model',
    vehicle_lic_state: 'License Plate State',
    vehicle_lic_number: 'License Plate Number',
    vin: 'VIN Number',
    proof_of_insurance: 'Proof of Insurance',
    emergency_contact_name: 'Emergency Contact',
    emergency_contact_phone: 'Emergency Phone',
    move_in_date: 'Move-In Date',
    agree_to_privacy_policy: 'Privacy Policy Agreement',
  }

  const getMissingFields = (): string[] => {
    const missing: string[] = []
    const values = getValues()
    if (!values.agree_to_privacy_policy) missing.push('Please agree to the privacy policy')
    if (!isValid && errors) {
      const errorKeys = Object.keys(errors)
      if (errorKeys.length > 0 && errorKeys.length <= 5) {
        errorKeys.forEach(key => {
          const label = FIELD_LABELS[key] || key
          missing.push(`${label} is required`)
        })
      } else if (errorKeys.length > 5) {
        missing.push(`${errorKeys.length} fields still need attention`)
      }
    }
    return missing
  }

  const onSubmit = async (values: StorageFormValues) => {
    // Re-entrancy guard — never let two submits run at once (belt-and-suspenders
    // on top of the disabled/isSubmitting button).
    if (submittingRef.current) return
    submittingRef.current = true

    logErrorToDb({
      event_name: 'form_submitted',
      error_message: 'Form submitted — preparing agreement',
      customer_email: values.email || authenticatedCustomer?.email,
      form_step: 6,
      payload_snapshot: { space_id: space?.id, flow: authenticatedCustomer?.id ? 'addendum' : 'new_customer' },
    })

    onSetPageSubtitle("... preparing the rental agreement with your information, please wait")
    let requestDescription = 'Prepare Agreement'
    try {
      const monthlyRate = space.monthly_rate + (values.need_24hr_access ? 15 : 0) + (values.need_3x_week_access ? 15 : 0)

      if (authenticatedCustomer?.id) {
        // ===== ADDENDUM FLOW =====
        console.log('Processing addendum for authenticated customer:', authenticatedCustomer.id)
        requestDescription = 'Prepare Addendum Agreement'
        const today = new Date()
        const todayFormatted = today.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })

        let primaryLease: any = null
        let primaryLeaseDate = todayFormatted

        try {
          const allLeasesNoFilter = await pb.collection('leases').getFullList()
          console.log('Total leases in database:', allLeasesNoFilter.length)

          primaryLease = await pb.collection('leases').getFirstListItem(
            `customer="${authenticatedCustomer.id}" && lease_type="primary" && status!="terminated"`,
            { expand: 'spaces' }
          )

          if (primaryLease?.created) {
            primaryLeaseDate = new Date(primaryLease.created).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
          }
        } catch (error: any) {
          if (error?.status === 401 || error?.status === 403) {
            pb.authStore.clear()
            onSessionExpired()
            return
          }
          throw new Error('You must have an active primary lease before adding additional vehicles. Please contact us at (614) 699-2495 for assistance.')
        }

        const primaryLeaseHas24hrAccess = primaryLease?.options?.includes('access-24-hrs') || false
        const primaryLeaseHas3xWeekAccess = primaryLease?.options?.includes('access-3x-week') || false

        let primarySpace = primaryLease.expand?.spaces?.[0]
        if (!primarySpace && primaryLease.spaces?.[0]) {
          try {
            primarySpace = await pb.collection('spaces').getOne(primaryLease.spaces[0])
          } catch {
            throw new Error('Could not find space information for your primary lease')
          }
        }
        if (!primarySpace) throw new Error('Could not find space information for your primary lease')

        const primaryLeaseMonthlyRate = primarySpace.monthly_rate +
          (primaryLeaseHas24hrAccess ? 15 : 0) + (primaryLeaseHas3xWeekAccess ? 15 : 0)

        const proratedRent = calculateProratedTotal(space.monthly_rate, values.need_24hr_access, values.need_3x_week_access)
        const newTotalMonthlyRent = primaryLeaseMonthlyRate + monthlyRate
        const nextPaymentDue = getNextFirstOfMonth()

        // Reuse an already-created agreement on retry instead of minting a duplicate.
        const submissionKey = computeSubmissionKey(values, space.id)
        let inviteResponse: any
        if (createdContractRef.current?.key === submissionKey && createdContractRef.current.invite) {
          inviteResponse = createdContractRef.current.invite
        } else {
          inviteResponse = await prepareAddendumAgreement({
            today_date: todayFormatted,
            master_lease_signed_date: primaryLeaseDate,
            first_name: authenticatedCustomer.first_name,
            last_name: authenticatedCustomer.last_name,
            phone: authenticatedCustomer.phone,
            email: authenticatedCustomer.email,
            business_name: authenticatedCustomer.business_name || '',
            vehicle_type: values.vehicle_type,
            vehicle_year: values.vehicle_year,
            vehicle_make: values.vehicle_make,
            vehicle_model: values.vehicle_model,
            vehicle_lic_state: values.vehicle_lic_state,
            vehicle_lic_number: values.vehicle_lic_number,
            vin: values.vin,
            vehicle_description: values.description || '',
            move_in_date: values.move_in_date,
            space_type: `${space.type} (${space.width}ft x ${space.length}ft) outdoor`,
            access_24_7: values.need_24hr_access ? 'yes' : 'no',
            access_3x_week: values.need_3x_week_access ? 'yes' : 'no',
            monthly_rate: monthlyRate.toFixed(2),
            prorated_rent: proratedRent.toFixed(2),
            next_payment_due: nextPaymentDue,
            new_total_monthly_rent: newTotalMonthlyRent.toFixed(2),
            redirect_url: `${window.location.href}&hasSigned=true`
          })

          if (!inviteResponse) throw new Error('Failed to prepare addendum agreement')
          createdContractRef.current = { key: submissionKey, invite: inviteResponse }
        }

        requestDescription = 'Create Lease Addendum'
        const addendumResponse: any = await createLeaseAddendum({
          customerId: authenticatedCustomer.id,
          vehicle: {
            type: values.vehicle_type, year: values.vehicle_year,
            make: values.vehicle_make, model: values.vehicle_model,
            lic_plate_state: values.vehicle_lic_state, lic_plate_number: values.vehicle_lic_number,
            vin: values.vin, description: values.description, proof_of_insurance: values.proof_of_insurance,
          },
          lease: {
            spaceId: space.id, move_in_date: values.move_in_date, move_out_date: values.move_out_date,
            options: [values.need_24hr_access ? "access-24-hrs" : null, values.need_3x_week_access ? "access-3x-week" : null].filter(Boolean) as string[],
            contract_ref_id: inviteResponse.id
          }
        })

        if (!addendumResponse.success) throw new Error(addendumResponse.error || 'Failed to create lease addendum')

        onSetFirstName(authenticatedCustomer.first_name)
        const signUrl = inviteResponse.signers[0].sign_page_url

        logErrorToDb({
          event_name: 'signing_redirect_attempted',
          error_message: signUrl ? `Signing URL generated: ${signUrl.substring(0, 80)}...` : 'ERROR: No signing URL returned',
          customer_email: authenticatedCustomer.email,
          form_step: 6,
          payload_snapshot: { flow: 'addendum', contract_id: inviteResponse.id, has_sign_url: !!signUrl },
        })

        onSetSignPageUrl(signUrl)
        onSetPageTitle(PREP_AGREEMENT_TITLE)
        onSetPageSubtitle(PLEASE_WAIT)
        window.scrollTo(0, 0)
        navigate(`/storage-form?id=${spaceId}&formSubmitted=true`)
      } else {
        // ===== FULL RESERVATION FLOW =====
        console.log('Processing full reservation for new customer')
        requestDescription = 'Prepare Agreement'

        // Reuse an already-created agreement on retry instead of minting a duplicate.
        const submissionKey = computeSubmissionKey(values, space.id)
        let inviteResponse: any
        if (createdContractRef.current?.key === submissionKey && createdContractRef.current.invite) {
          inviteResponse = createdContractRef.current.invite
        } else {
          inviteResponse = await prepareAgreement({
            ...values,
            monthly_rate: monthlyRate,
            space_id: space.id,
            space_type: `${space.type} (${space.width}ft x ${space.length}ft) outdoor`,
            access_24_7: values.need_24hr_access ? 'yes' : 'no',
            access_3x_week: values.need_3x_week_access ? 'yes' : 'no',
            redirect_url: `${window.location.href}&hasSigned=true`
          })

          if (!inviteResponse) throw new Error('Failed to prepare agreement')
          createdContractRef.current = { key: submissionKey, invite: inviteResponse }
        }

        requestDescription = 'Create Reservation'
        const randPassword = `${values.last_name}${values.last_name}${Math.random().toString(36).substring(2, 14)}`

        const reservationResponse: any = await createReservation({
          customer: {
            username: `${values.first_name}${values.last_name}-${values.phone.slice(values.phone.length - 4, values.phone.length)}`.toLowerCase(),
            email: values.email, password: randPassword, passwordConfirm: randPassword,
            first_name: values.first_name, last_name: values.last_name,
            phone: formatPhoneNumber(values.phone), business_name: values.business_name,
            address_line_1: values.address_line_1, address_line_2: values.address_line_2,
            city: values.city, state: values.state, zip: values.zip,
            emergency_contact_name: values.emergency_contact_name,
            emergency_contact_phone: values.emergency_contact_phone && formatPhoneNumber(values.emergency_contact_phone),
            driver_lic_front: values.driver_lic_front, driver_lic_back: values.driver_lic_back,
          },
          vehicle: {
            type: values.vehicle_type, year: values.vehicle_year,
            make: values.vehicle_make, model: values.vehicle_model,
            lic_plate_state: values.vehicle_lic_state, lic_plate_number: values.vehicle_lic_number,
            vin: values.vin, description: values.description, proof_of_insurance: values.proof_of_insurance,
          },
          lease: {
            spaceId: space.id, move_in_date: values.move_in_date, move_out_date: values.move_out_date,
            options: [values.need_24hr_access ? "access-24-hrs" : null, values.need_3x_week_access ? "access-3x-week" : null].filter(Boolean) as string[],
            contract_ref_id: inviteResponse.id
          }
        })

        if (!reservationResponse.success) {
          if (reservationResponse.code === 'EMAIL_EXISTS') {
            // Stash everything needed to complete after OTP, then hand off to inline OTP.
            pendingReservationRef.current = {
              values,
              contractRefId: createdContractRef.current?.invite?.id,
              signUrl: createdContractRef.current?.invite?.signers?.[0]?.sign_page_url,
            }
            dispatchRecovery({ type: 'EMAIL_EXISTS', email: values.email })
            onSetPageSubtitle(undefined)
            return // hand off to inline OTP UI; submittingRef released in finally
          }
          throw new Error(reservationResponse.error || 'Failed to create reservation')
        }

        onSetFirstName(reservationResponse.customer.first_name)
        const signUrl = inviteResponse.signers[0].sign_page_url

        logErrorToDb({
          event_name: 'signing_redirect_attempted',
          error_message: signUrl ? `Signing URL generated: ${signUrl.substring(0, 80)}...` : 'ERROR: No signing URL returned',
          customer_email: values.email,
          form_step: 6,
          payload_snapshot: { flow: 'new_customer', contract_id: inviteResponse.id, has_sign_url: !!signUrl },
        })

        onSetSignPageUrl(signUrl)
        onSetPageTitle(PREP_AGREEMENT_TITLE)
        onSetPageSubtitle(PLEASE_WAIT)
        window.scrollTo(0, 0)
        navigate(`/storage-form?id=${spaceId}&formSubmitted=true`)
      }
    } catch (error: any) {
      console.error("Error creating vehicle [JSON]:", JSON.stringify(error, null, 2))
      onSetPageSubtitle(undefined)

      if (error?.status === 401 || error?.status === 403 || error?.response?.status === 401 || error?.response?.status === 403) {
        pb.authStore.clear()
        onSessionExpired()
        return
      }

      // GUARANTEE a non-empty, useful message — the modal must never render blank.
      const errorMsg = buildSubmitErrorMessage(error, requestDescription)

      onError(errorMsg)

      logErrorToDb({
        event_name: 'form_submission_error',
        error_message: errorMsg,
        customer_email: values.email || authenticatedCustomer?.email,
        form_step: 6,
        error_details: { requestDescription, status: error?.status || error?.response?.status },
        payload_snapshot: { flow: authenticatedCustomer?.id ? 'addendum' : 'new_customer' },
      })
    } finally {
      submittingRef.current = false
    }
  }

  const handleRecoveryVerified = async (customerId: string, token: string, status?: string) => {
    if (completingRef.current) return
    completingRef.current = true
    try {
      // Authenticate the browser session as this customer.
      pb.authStore.save(token, null)

      // Active-lease detection is based on the customer status returned by the OTP
      // verify — the browser auth can't satisfy the leases listRule, so we must not
      // query leases from here.
      const hasActiveLease = status === 'active'

      dispatchRecovery({ type: 'VERIFIED', token, customerId, hasActiveLease })

      const pending = pendingReservationRef.current
      if (hasActiveLease) {
        // They already have an active account — this submission can't be auto-resumed
        // as a new primary lease. The eSignatures contract created earlier in this
        // submit is now orphaned; log it so the owner has visibility.
        logErrorToDb({
          event_name: 'recovery_active_customer_orphan_contract',
          error_message: 'Active customer used the new-customer form; primary contract orphaned',
          customer_email: pending?.values?.email,
          form_step: 6,
          payload_snapshot: { contract_ref_id: pending?.contractRefId },
        })
        onError('You already have an active account with us. Please log in from the menu to add another vehicle.')
        onLoginClick()
        dispatchRecovery({ type: 'RESET' })
        return
      }

      // RESUME: complete the reservation for the existing (incomplete) customer,
      // reusing the contract already created in this submit.
      const v = pending.values
      const completion: any = await createReservation({
        customerAuthToken: token,
        customer: {
          username: `${v.first_name}${v.last_name}-${v.phone.slice(-4)}`.toLowerCase(),
          email: v.email, password: '', passwordConfirm: '',
          first_name: v.first_name, last_name: v.last_name, phone: formatPhoneNumber(v.phone),
          business_name: v.business_name,
          address_line_1: v.address_line_1, address_line_2: v.address_line_2,
          city: v.city, state: v.state, zip: v.zip,
          emergency_contact_name: v.emergency_contact_name,
          emergency_contact_phone: v.emergency_contact_phone && formatPhoneNumber(v.emergency_contact_phone),
          driver_lic_front: v.driver_lic_front, driver_lic_back: v.driver_lic_back,
        },
        vehicle: {
          type: v.vehicle_type, year: v.vehicle_year, make: v.vehicle_make, model: v.vehicle_model,
          lic_plate_state: v.vehicle_lic_state, lic_plate_number: v.vehicle_lic_number,
          vin: v.vin, description: v.description, proof_of_insurance: v.proof_of_insurance,
        },
        lease: {
          spaceId: space.id, move_in_date: v.move_in_date, move_out_date: v.move_out_date,
          options: [v.need_24hr_access ? 'access-24-hrs' : null, v.need_3x_week_access ? 'access-3x-week' : null].filter(Boolean) as string[],
          contract_ref_id: pending.contractRefId,
        },
      })

      if (!completion.success) throw new Error(completion.error || 'Failed to complete your reservation')

      onSetFirstName(v.first_name)
      onSetSignPageUrl(pending.signUrl)
      onSetPageTitle(PREP_AGREEMENT_TITLE)
      onSetPageSubtitle(PLEASE_WAIT)
      window.scrollTo(0, 0)
      navigate(`/storage-form?id=${spaceId}&formSubmitted=true`)
    } catch (err: any) {
      onError(buildSubmitErrorMessage(err, 'Complete reservation'))
      dispatchRecovery({ type: 'RESET' })
    } finally {
      completingRef.current = false
    }
  }

  const watchAgreeToPolicy = watch('agree_to_privacy_policy')

  if (recovery.phase === 'otp') {
    return (
      <OtpVerificationStep
        firstName={getValues('first_name')}
        lastName={getValues('last_name')}
        email={recovery.email || getValues('email')}
        phone={getValues('phone')}
        sendOtp={sendExistingCustomerOTP}
        verifyOtp={verifyCustomerOtpResume}
        onVerified={handleRecoveryVerified}
        onBack={() => dispatchRecovery({ type: 'RESET' })}
        onError={onError}
      />
    )
  }

  if (recovery.phase === 'completing' || recovery.phase === 'addendum') {
    // Don't flash the wizard back after a successful verify — show a standby panel
    // while we finalize (or, for the active-account case, while the login message overlays).
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', padding: '3rem 0' }}>
        <Spinner />
        <p className="forceBlack" style={{ fontSize: '1.1rem' }}>Finalizing your reservation…</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <StepProgressIndicator currentStep={currentStep} totalSteps={totalSteps} stepTitles={stepTitles} />

      {currentStepId === 1 && (
        <PersonalInfoStep register={register} errors={errors} oauthUser={oauthUser}
          isAuthenticated={isAuthenticated} onLoginClick={onLoginClick} />
      )}
      {currentStepId === 2 && (
        <AddressInfoStep register={register} errors={errors} setValue={setValue} />
      )}
      {currentStepId === 3 && (
        <VehicleInfoStep register={register} errors={errors} setValue={setValue} />
      )}
      {currentStepId === 4 && (
        <EmergencyContactStep register={register} errors={errors} />
      )}
      {currentStepId === 5 && (
        <LeaseDetailsStep register={register} errors={errors} watch={watch} space={space} />
      )}
      {currentStepId === 6 && (
        <PricingSummaryStep register={register} watch={watch} space={space} />
      )}

      <WizardNavigation
        currentStep={currentStep} totalSteps={totalSteps}
        onBack={handleBack} onNext={handleNext}
        isSubmitting={isSubmitting} dirty={isDirty} isValid={isValid}
        agreeToPolicy={watchAgreeToPolicy}
        missingFields={getMissingFields()}
      />
    </form>
  )
}

export default InnerForm
